import { formatLicensePlate, formatPlaceOfInsurance } from "@/utils/formatUtils";
import { tInterval } from "@/utils/translationUtils";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatLifeInsuranceTariff } from "../../../../../lifeinsurancetariff/utils";
import { InsuranceType } from "../../../../enums";
import { Insurance, InsuranceContract } from "../../../../types";
import { parseVehicleModelName } from "../../../../utils";

interface Props {
  insurance: Insurance;
  contract: InsuranceContract;
}

const InsuranceHeaderView = ({ insurance, contract }: Props) => {
  const { t } = useTranslation();

  const label = useMemo(() => {
    switch (insurance.type) {
      case InsuranceType.MTPL:
      case InsuranceType.CRASH:
      case InsuranceType.GAP:
      case InsuranceType.PAS:
        const { vehicle, licensePlate } = insurance;

        return (
          <span>
            {[
              parseVehicleModelName({
                customModel: vehicle.customModel,
                customBrand: vehicle.customBrand,
                brandName: vehicle.model.brand.name,
                modelName: vehicle.model.name,
              }),
              licensePlate ? formatLicensePlate(licensePlate) : undefined,
              vehicle.vin,
            ]
              .filter((v) => !!v)
              .join(" | ")}
          </span>
        );
      case InsuranceType.VEHICLE:
        return (
          <span>
            {[insurance.licensePlate ? formatLicensePlate(insurance.licensePlate) : undefined, insurance.vin]
              .filter((v) => !!v)
              .join(" | ")}
          </span>
        );
      case InsuranceType.REALTY:
        const realtyData = insurance.insuranceData;
        const realtyHouseholdLabel =
          realtyData.realty && realtyData.household
            ? t("contract.helpers.realtyAndHousehold")
            : realtyData.realty
              ? t("contract.attrs.insurances.insuranceData.realty._label")
              : t("contract.attrs.insurances.insuranceData.household._label");

        return (
          <span>
            {[
              t("calc.realty.enums.buildingType." + realtyData.buildingType),
              formatPlaceOfInsurance(realtyData.placeOfInsurance),
              realtyHouseholdLabel,
            ].join(" | ")}
          </span>
        );
      case InsuranceType.TRAVEL:
        const travelData = insurance.insuranceData;

        return (
          <span>
            {[
              t("calc.travel.enums.territorialValidity." + travelData.territorialValidity),
              tInterval("contract.helpers.insuredClientsCount_interval", travelData.insuredClients.length),
            ].join(" | ")}
          </span>
        );
      case InsuranceType.LIFE:
        return <span>{formatLifeInsuranceTariff(insurance.tariff)}</span>;
      case InsuranceType.GENERIC:
        return <span>{contract.product.name}</span>;
      default:
        return "";
    }
  }, [insurance, contract]);

  return <>{label}</>;
};

export default InsuranceHeaderView;
